import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const BackIcon = () => (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>);
const PlusIcon = () => (<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>);
const TrashIcon = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>);
const EditIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>);
const UploadIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>);
const ChevronRight = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>);
const ChevronDown = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>);

const DIFF_LABELS = ['','Beginner','Basic','Intermediate','Advanced','Expert'];
const DIFF_COLORS = ['','#22C55E','#4BC8F5','#F9C74F','#FF6B35','#FF4D00'];

const inputStyle = { width:'100%', background:'#1C1916', border:'1.5px solid #2E2924', borderRadius:8, color:'#F0EBE3', fontFamily:"'Barlow', sans-serif", fontSize:14, padding:'10px 12px', outline:'none' };
const labelStyle = { fontSize:11, color:'#7A7268', fontFamily:"'Barlow Condensed', sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', display:'block', marginBottom:6 };

function Field({ label, children }) {
  return <div style={{ marginBottom:14 }}><label style={labelStyle}>{label}</label>{children}</div>;
}

function ImagePicker({ label, currentUrl, onUpload, bucket, pathPrefix }) {
  const ref = useRef();
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentUrl || '');

  const handle = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `${pathPrefix}-${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from(bucket).upload(path, file, { upsert:true });
      if (error) throw error;
      const { data:{ publicUrl } } = supabase.storage.from(bucket).getPublicUrl(path);
      setPreview(publicUrl);
      onUpload(publicUrl);
    } catch (err) { alert('Upload error: ' + err.message); }
    setUploading(false);
  };

  return (
    <Field label={label}>
      <div style={{ display:'flex', gap:10, alignItems:'center' }}>
        {preview
          ? <img src={preview} alt="" style={{ width:52, height:52, objectFit:'cover', objectPosition:'50% 24%', borderRadius:8, border:'1px solid #2E2924', flexShrink:0 }}/>
          : <div style={{ width:52, height:52, background:'#2A2520', borderRadius:8, border:'1.5px dashed #3A3530', display:'flex', alignItems:'center', justifyContent:'center', color:'#5A5248', flexShrink:0 }}><UploadIcon/></div>
        }
        <div>
          <button onClick={() => ref.current.click()} style={{ background:'#2A2520', color:'#C4B9AC', padding:'7px 12px', borderRadius:8, fontFamily:"'Barlow Condensed'", fontWeight:700, fontSize:12, letterSpacing:1, display:'flex', alignItems:'center', gap:5, border:'none', cursor:'pointer' }}>
            <UploadIcon/> {uploading ? 'LOADING…' : preview ? 'CHANGE' : 'UPLOAD'}
          </button>
          {preview && <div style={{ fontSize:11, color:'#22C55E', marginTop:4 }}>✓ Image uploaded</div>}
        </div>
        <input ref={ref} type="file" accept="image/*" onChange={handle} style={{ display:'none' }}/>
      </div>
    </Field>
  );
}

function Modal({ title, onClose, onSave, saving, children }) {
  return (
    <div style={{ position:'fixed', inset:0, zIndex:200, background:'rgba(0,0,0,0.87)', display:'flex', alignItems:'flex-end', backdropFilter:'blur(4px)' }}>
      <div style={{ background:'#171410', borderRadius:'20px 20px 0 0', padding:'28px 20px 44px', width:'100%', maxHeight:'92vh', overflowY:'auto' }}>
        <div style={{ fontFamily:"'Barlow Condensed'", fontSize:20, fontWeight:800, letterSpacing:2, marginBottom:20, color:'#F0EBE3' }}>{title}</div>
        {children}
        <div style={{ display:'flex', gap:10, marginTop:24 }}>
          <button onClick={onClose} style={{ flex:1, padding:'13px', background:'#2A2520', color:'#9A9088', borderRadius:10, fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:1, fontSize:14, border:'none', cursor:'pointer' }}>CANCEL</button>
          <button onClick={onSave} disabled={saving} style={{ flex:2, padding:'13px', background:'#FF5500', color:'#fff', borderRadius:10, fontFamily:"'Barlow Condensed'", fontWeight:800, letterSpacing:2, fontSize:14, border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
            {saving ? <div style={{ width:18, height:18, borderRadius:'50%', border:'2.5px solid rgba(255,255,255,0.3)', borderTopColor:'#fff', animation:'spin 0.7s linear infinite' }}/> : 'SAVE'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RowActions({ onEdit, onDelete }) {
  return (
    <div style={{ display:'flex', gap:5, flexShrink:0 }}>
      <button onClick={onEdit} style={{ background:'#2A2520', color:'#9A9088', width:30, height:30, borderRadius:7, display:'flex', alignItems:'center', justifyContent:'center', border:'none', cursor:'pointer' }}><EditIcon/></button>
      <button onClick={onDelete} style={{ background:'#2A1010', color:'#FF6666', width:30, height:30, borderRadius:7, display:'flex', alignItems:'center', justifyContent:'center', border:'none', cursor:'pointer' }}><TrashIcon/></button>
    </div>
  );
}

const ADMIN_PIN = '6870';

export default function AdminPanel() {
  const navigate = useNavigate();
  const [pinUnlocked, setPinUnlocked] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState(false);
  const [tab, setTab] = useState('categories');
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [expandedCats, setExpandedCats] = useState([]);
  const [expandedExCats, setExpandedExCats] = useState([]);

  const [catModal, setCatModal] = useState(false);
  const [subModal, setSubModal] = useState(false);
  const [exModal, setExModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [updatingUser, setUpdatingUser] = useState(null);
  const [catForm, setCatForm] = useState({ id:null, name:'', emoji:'🥁', color:'#FF5500', image_url:'' });
  const [subForm, setSubForm] = useState({ id:null, name:'', category_id:'' });
  const [exForm, setExForm] = useState({ id:null, title:'', description:'', youtube_url:'', image_url:'', default_bpm:80, default_time_sig:'4/4', difficulty:2, category_id:'', subcategory_id:'', active:true });

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { if (tab === 'users' && pinUnlocked) loadUsers(); }, [tab, pinUnlocked]);

  const loadAll = async () => {
    const [{ data:cats }, { data:subs }, { data:exs }] = await Promise.all([
      supabase.from('categories').select('*').order('sort_order'),
      supabase.from('subcategories').select('*').order('sort_order'),
      supabase.from('exercises').select('*, categories(name,color), subcategories(name)').order('id', { ascending:false }),
    ]);
    if (cats) setCategories(cats);
    if (subs) setSubcategories(subs);
    if (exs) setExercises(exs);
  };

  const loadUsers = async () => {
    setLoadingUsers(true);
    const { data } = await supabase
      .from('profiles')
      .select('id, name, email, plan, trial_ends_at, plan_granted_by, created_at')
      .order('created_at', { ascending: false });
    if (data) setUsers(data);
    setLoadingUsers(false);
  };

  const updateUserPlan = async (userId, plan, grantedBy = 'admin') => {
    setUpdatingUser(userId);
    await supabase.from('profiles').update({ plan, plan_granted_by: grantedBy }).eq('id', userId);
    await loadUsers();
    setUpdatingUser(null);
  };

  // Categories
  const openNewCat = () => { setCatForm({ id:null, name:'', emoji:'🥁', color:'#FF5500', image_url:'' }); setCatModal(true); };
  const openEditCat = (c) => { setCatForm({ id:c.id, name:c.name, emoji:c.emoji||'', color:c.color||'#FF5500', image_url:c.image_url||'' }); setCatModal(true); };
  const saveCat = async () => {
    if (!catForm.name.trim()) return;
    setSaving(true);
    const d = { name:catForm.name, emoji:catForm.emoji, color:catForm.color, image_url:catForm.image_url };
    if (catForm.id) await supabase.from('categories').update(d).eq('id', catForm.id);
    else await supabase.from('categories').insert({ ...d, sort_order:categories.length+1 });
    setSaving(false); setCatModal(false); loadAll();
  };
  const deleteCat = async (id) => {
    if (!window.confirm('Delete category? All subcategories and exercises will be deleted.')) return;
    await supabase.from('categories').delete().eq('id', id);
    loadAll();
  };

  // Subcategories
  const openNewSub = (catId) => { setSubForm({ id:null, name:'', category_id:catId||'' }); setSubModal(true); };
  const openEditSub = (s) => { setSubForm({ id:s.id, name:s.name, category_id:s.category_id }); setSubModal(true); };
  const saveSub = async () => {
    if (!subForm.name.trim() || !subForm.category_id) return;
    setSaving(true);
    const d = { name:subForm.name, category_id:parseInt(subForm.category_id) };
    if (subForm.id) await supabase.from('subcategories').update(d).eq('id', subForm.id);
    else await supabase.from('subcategories').insert(d);
    setSaving(false); setSubModal(false); loadAll();
  };
  const deleteSub = async (id) => {
    if (!window.confirm('Delete subcategory and all associated exercises?')) return;
    await supabase.from('subcategories').delete().eq('id', id);
    loadAll();
  };

  // Exercises
  const openNewEx = (catId, subId) => { setExForm({ id:null, title:'', description:'', youtube_url:'', image_url:'', default_bpm:80, default_time_sig:'4/4', difficulty:2, category_id:catId||'', subcategory_id:subId||'', active:true }); setExModal(true); };
  const openEditEx = (ex) => { setExForm({ id:ex.id, title:ex.title, description:ex.description||'', youtube_url:ex.youtube_url||'', image_url:ex.image_url||'', default_bpm:ex.default_bpm, default_time_sig:ex.default_time_sig||'4/4', difficulty:ex.difficulty, category_id:ex.category_id, subcategory_id:ex.subcategory_id||'', active:ex.active }); setExModal(true); };
  const saveEx = async () => {
    if (!exForm.title.trim()) return;
    setSaving(true);
    const d = { title:exForm.title, description:exForm.description, youtube_url:exForm.youtube_url||null, image_url:exForm.image_url, default_bpm:parseInt(exForm.default_bpm), default_time_sig:exForm.default_time_sig||'4/4', difficulty:parseInt(exForm.difficulty), category_id:parseInt(exForm.category_id)||null, subcategory_id:parseInt(exForm.subcategory_id)||null, active:exForm.active };
    if (exForm.id) await supabase.from('exercises').update(d).eq('id', exForm.id);
    else await supabase.from('exercises').insert(d);
    setSaving(false); setExModal(false); loadAll();
  };
  const deleteEx = async (id) => {
    if (!window.confirm('Delete exercise?')) return;
    await supabase.from('exercises').delete().eq('id', id);
    loadAll();
  };
  const toggleActive = async (ex) => {
    await supabase.from('exercises').update({ active:!ex.active }).eq('id', ex.id);
    loadAll();
  };

  const toggleCat = (id) => setExpandedCats(p => p.includes(id) ? p.filter(x=>x!==id) : [...p,id]);
  const toggleExCat = (id) => setExpandedExCats(p => p.includes(id) ? p.filter(x=>x!==id) : [...p,id]);
  const filteredSubs = subcategories.filter(s => s.category_id === parseInt(exForm.category_id));

  const handlePin = () => {
    if (pinInput === ADMIN_PIN) {
      setPinUnlocked(true);
      setPinError(false);
    } else {
      setPinError(true);
      setPinInput('');
    }
  };

  if (!pinUnlocked) return (
    <div style={{ fontFamily:"'Barlow', sans-serif", background:'#111009', minHeight:'100vh', color:'#F0EBE3', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'24px 20px' }}>
      <div className="bg-grid"/>
      <div style={{ width:'100%', maxWidth:320, background:'#171410', border:'1px solid #2A2520', borderRadius:20, padding:'36px 28px', position:'relative', zIndex:1, textAlign:'center' }}>
        <div style={{ fontSize:40, marginBottom:16 }}>🔒</div>
        <div style={{ fontFamily:"'Barlow Condensed'", fontSize:26, fontWeight:800, letterSpacing:3, color:'#F0EBE3', marginBottom:6 }}>ADMIN ACCESS</div>
        <div style={{ fontSize:13, color:'#5A5650', marginBottom:28 }}>Enter the access PIN to continue.</div>
        <input
          type="password" inputMode="numeric" maxLength={6}
          value={pinInput} onChange={e => { setPinInput(e.target.value); setPinError(false); }}
          onKeyDown={e => e.key === 'Enter' && handlePin()}
          placeholder="PIN"
          style={{ width:'100%', background:'#1C1916', border:`1.5px solid ${pinError ? '#FF4444' : '#2E2924'}`, borderRadius:10, color:'#F0EBE3', fontFamily:"'Barlow Condensed'", fontSize:28, fontWeight:700, letterSpacing:10, textAlign:'center', padding:'14px 12px', outline:'none', boxSizing:'border-box', marginBottom:8 }}
        />
        {pinError && <div style={{ fontSize:12, color:'#FF6060', marginBottom:12 }}>Incorrect PIN. Try again.</div>}
        {!pinError && <div style={{ marginBottom:12 }}/>}
        <button onClick={handlePin} style={{ width:'100%', padding:'13px', background:'#FF5500', borderRadius:10, color:'#fff', fontFamily:"'Barlow Condensed'", fontWeight:800, fontSize:16, letterSpacing:2, border:'none', cursor:'pointer' }}>
          ENTER
        </button>
        <button onClick={() => navigate(-1)} style={{ marginTop:16, background:'transparent', color:'#5A5650', fontSize:13, fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:1, border:'none', cursor:'pointer' }}>← BACK</button>
      </div>
    </div>
  );

  return (
    <div style={{ fontFamily:"'Barlow', sans-serif", background:'#111009', minHeight:'100vh', color:'#F0EBE3', maxWidth:460, margin:'0 auto', paddingBottom:40 }}>
      <div className="bg-grid"/>

      {/* Header */}
      <div style={{ padding:'52px 20px 16px', background:'linear-gradient(180deg, #0A0907 0%, #111009 100%)', position:'relative', zIndex:1 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button onClick={() => navigate('/')} style={{ background:'transparent', color:'#7A7268', border:'none', cursor:'pointer' }}><BackIcon/></button>
          <div style={{ flex:1 }}>
            <div style={{ fontFamily:"'Barlow Condensed'", fontSize:11, fontWeight:700, letterSpacing:2, color:'#FF5500' }}>DRUMNASIUM</div>
            <h1 style={{ fontFamily:"'Barlow Condensed'", fontSize:28, fontWeight:800, letterSpacing:2, color:'#F0EBE3', lineHeight:1 }}>ADMIN</h1>
          </div>
          <div style={{ textAlign:'right' }}>
            <div style={{ fontFamily:"'Barlow Condensed'", fontSize:13, fontWeight:700, color:'#FF5500' }}>{categories.length} CAT</div>
            <div style={{ fontFamily:"'Barlow Condensed'", fontSize:13, fontWeight:700, color:'#9A9088' }}>{exercises.length} EX</div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', gap:4, marginTop:16, background:'#1C1916', borderRadius:10, padding:4 }}>
          {[['categories','Categories'],['subcategories','Subcategories'],['exercises','Exercises'],['users','Users']].map(([id,label]) => (
            <button key={id} onClick={() => setTab(id)} style={{ flex:1, padding:'9px 4px', background:tab===id?'#FF5500':'transparent', color:tab===id?'#fff':'#7A7268', borderRadius:8, fontFamily:"'Barlow Condensed'", fontWeight:700, fontSize:11, letterSpacing:0.5, border:'none', cursor:'pointer', transition:'all 0.15s' }}>{label}</button>
          ))}
        </div>
      </div>

      <div style={{ padding:'16px', position:'relative', zIndex:1 }}>

        {/* ── CATEGORIES ── */}
        {tab === 'categories' && (
          <>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
              <span style={{ fontSize:12, color:'#7A7268', fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:2 }}>{categories.length} CATEGORIAS</span>
              <button onClick={openNewCat} style={{ background:'#FF5500', color:'#fff', padding:'8px 14px', borderRadius:8, display:'flex', alignItems:'center', gap:5, fontFamily:"'Barlow Condensed'", fontWeight:800, fontSize:12, letterSpacing:1, border:'none', cursor:'pointer' }}><PlusIcon/> NOVA</button>
            </div>
            {categories.map(cat => (
              <div key={cat.id} style={{ background:'#1C1916', border:'1px solid #2A2520', borderRadius:12, marginBottom:8, padding:'14px 16px', display:'flex', alignItems:'center', gap:12 }}>
                {cat.image_url
                  ? <img src={cat.image_url} alt="" style={{ width:44, height:44, borderRadius:9, objectFit:'cover', objectPosition:'50% 24%', flexShrink:0, border:'1px solid #2E2924' }}/>
                  : <div style={{ width:44, height:44, borderRadius:9, background:(cat.color||'#FF5500')+'22', border:`1.5px solid ${cat.color||'#FF5500'}44`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>{cat.emoji||'🥁'}</div>
                }
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:15, fontWeight:700, color:'#F0EBE3' }}>{cat.name}</div>
                  <div style={{ fontSize:11, color:'#5A5248', marginTop:2 }}>
                    {subcategories.filter(s=>s.category_id===cat.id).length} subcategories · {exercises.filter(e=>e.category_id===cat.id).length} exercises
                  </div>
                </div>
                <RowActions onEdit={() => openEditCat(cat)} onDelete={() => deleteCat(cat.id)}/>
              </div>
            ))}
            {categories.length === 0 && <div style={{ textAlign:'center', padding:'40px 0', color:'#3A3530', fontSize:13, fontStyle:'italic' }}>Sem categories. Cria a primeira!</div>}
          </>
        )}

        {/* ── SUBCATEGORIES ── */}
        {tab === 'subcategories' && (
          <>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
              <span style={{ fontSize:12, color:'#7A7268', fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:2 }}>{subcategories.length} SUBCATEGORIES</span>
              <button onClick={() => openNewSub('')} style={{ background:'#FF5500', color:'#fff', padding:'8px 14px', borderRadius:8, display:'flex', alignItems:'center', gap:5, fontFamily:"'Barlow Condensed'", fontWeight:800, fontSize:12, letterSpacing:1, border:'none', cursor:'pointer' }}><PlusIcon/> NOVA</button>
            </div>
            {categories.map(cat => {
              const subs = subcategories.filter(s => s.category_id === cat.id);
              const isOpen = expandedCats.includes(cat.id);
              return (
                <div key={cat.id} style={{ background:'#1C1916', border:'1px solid #2A2520', borderRadius:12, marginBottom:8, overflow:'hidden' }}>
                  <div onClick={() => toggleCat(cat.id)} style={{ padding:'13px 16px', display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>
                    <div style={{ width:32, height:32, borderRadius:7, background:(cat.color||'#FF5500')+'22', display:'flex', alignItems:'center', justifyContent:'center', fontSize:17, flexShrink:0 }}>
                      {cat.image_url ? <img src={cat.image_url} alt="" style={{ width:32, height:32, borderRadius:7, objectFit:'cover', objectPosition:'50% 24%' }}/> : cat.emoji||'🥁'}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:14, fontWeight:700, color:'#F0EBE3' }}>{cat.name}</div>
                    </div>
                    <span style={{ fontSize:12, color:'#5A5248', marginRight:6 }}>{subs.length}</span>
                    <button onClick={(e) => { e.stopPropagation(); openNewSub(cat.id); }} style={{ background:'#2A2520', color:'#9A9088', width:26, height:26, borderRadius:6, display:'flex', alignItems:'center', justifyContent:'center', border:'none', cursor:'pointer', marginRight:4 }}><PlusIcon/></button>
                    <div style={{ color:'#5A5248' }}>{isOpen ? <ChevronDown/> : <ChevronRight/>}</div>
                  </div>
                  {isOpen && (
                    <div style={{ borderTop:'1px solid #2A2520', padding:'8px 16px 12px' }}>
                      {subs.length === 0 && <div style={{ fontSize:12, color:'#3A3530', fontStyle:'italic', padding:'6px 0' }}>No subcategories.</div>}
                      {subs.map(sub => (
                        <div key={sub.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 0', borderBottom:'1px solid #1E1C18' }}>
                          <div style={{ width:7, height:7, borderRadius:'50%', background:cat.color||'#FF5500', flexShrink:0 }}/>
                          <span style={{ flex:1, fontSize:13, color:'#C4B9AC' }}>{sub.name}</span>
                          <span style={{ fontSize:11, color:'#3A3530', marginRight:4 }}>{exercises.filter(e=>e.subcategory_id===sub.id).length} ex.</span>
                          <RowActions onEdit={() => openEditSub(sub)} onDelete={() => deleteSub(sub.id)}/>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {categories.length === 0 && <div style={{ textAlign:'center', padding:'40px 0', color:'#3A3530', fontSize:13, fontStyle:'italic' }}>Create a category first.</div>}
          </>
        )}

        {/* ── EXERCISES ── */}
        {tab === 'exercises' && (
          <>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
              <span style={{ fontSize:12, color:'#7A7268', fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:2 }}>{exercises.length} EXERCISES</span>
              <button onClick={() => openNewEx('','')} style={{ background:'#FF5500', color:'#fff', padding:'8px 14px', borderRadius:8, display:'flex', alignItems:'center', gap:5, fontFamily:"'Barlow Condensed'", fontWeight:800, fontSize:12, letterSpacing:1, border:'none', cursor:'pointer' }}><PlusIcon/> NOVO</button>
            </div>
            {categories.map(cat => {
              const catExs = exercises.filter(e => e.category_id === cat.id);
              if (catExs.length === 0) return null;
              const isOpen = expandedExCats.includes(cat.id);
              return (
                <div key={cat.id} style={{ marginBottom:10 }}>
                  <div onClick={() => toggleExCat(cat.id)} style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 6px', cursor:'pointer', borderRadius:8 }}>
                    <div style={{ width:8, height:8, borderRadius:'50%', background:cat.color||'#FF5500' }}/>
                    <span style={{ fontFamily:"'Barlow Condensed'", fontSize:13, fontWeight:800, color:'#9A9088', letterSpacing:1, flex:1 }}>{cat.name.toUpperCase()}</span>
                    <span style={{ fontSize:11, color:'#3A3530', marginRight:4 }}>{catExs.length}</span>
                    <div style={{ color:'#5A5248' }}>{isOpen ? <ChevronDown/> : <ChevronRight/>}</div>
                  </div>
                  {isOpen && catExs.map(ex => (
                    <div key={ex.id} style={{ background:'#1C1916', border:'1px solid #2A2520', borderRadius:10, padding:'11px 13px', marginBottom:5, display:'flex', alignItems:'center', gap:10, opacity:ex.active?1:0.45 }}>
                      {ex.image_url
                        ? <img src={ex.image_url} alt="" style={{ width:42, height:42, objectFit:'cover', objectPosition:'50% 24%', borderRadius:8, flexShrink:0 }}/>
                        : <div style={{ width:42, height:42, background:'#2A2520', borderRadius:8, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 }}>🥁</div>
                      }
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:700, color:'#F0EBE3', marginBottom:2 }}>{ex.title}</div>
                        <div style={{ fontSize:11, color:'#5A5248' }}>
                          {ex.subcategories?.name && <span>{ex.subcategories.name} · </span>}
                          <span style={{ color:DIFF_COLORS[ex.difficulty] }}>{DIFF_LABELS[ex.difficulty]}</span>
                          <span> · ♩ {ex.default_bpm}</span>
                        </div>
                      </div>
                      <div style={{ display:'flex', gap:4 }}>
                        <button onClick={() => toggleActive(ex)} style={{ background:ex.active?'rgba(255,85,0,0.1)':'#2A2520', color:ex.active?'#FF5500':'#5A5248', width:29, height:29, borderRadius:7, display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, border:'none', cursor:'pointer' }}>{ex.active?'👁':'🙈'}</button>
                        <button onClick={() => openEditEx(ex)} style={{ background:'#2A2520', color:'#9A9088', width:29, height:29, borderRadius:7, display:'flex', alignItems:'center', justifyContent:'center', border:'none', cursor:'pointer' }}><EditIcon/></button>
                        <button onClick={() => deleteEx(ex.id)} style={{ background:'#2A1010', color:'#FF6666', width:29, height:29, borderRadius:7, display:'flex', alignItems:'center', justifyContent:'center', border:'none', cursor:'pointer' }}><TrashIcon/></button>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
            {exercises.filter(e=>!e.category_id).map(ex => (
              <div key={ex.id} style={{ background:'#1C1916', border:'1px solid #2A2520', borderRadius:10, padding:'11px 13px', marginBottom:5, display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ flex:1 }}><div style={{ fontSize:13, fontWeight:700, color:'#F0EBE3' }}>{ex.title}</div></div>
                <RowActions onEdit={() => openEditEx(ex)} onDelete={() => deleteEx(ex.id)}/>
              </div>
            ))}
            {exercises.length === 0 && <div style={{ textAlign:'center', padding:'40px 0', color:'#3A3530', fontSize:13, fontStyle:'italic' }}>Sem exercises. Cria o primeiro!</div>}
          </>
        )}
      </div>

      {/* ── CATEGORY MODAL ── */}
      {catModal && (
        <Modal title={catForm.id?'EDITAR CATEGORIA':'NOVA CATEGORIA'} onClose={() => setCatModal(false)} onSave={saveCat} saving={saving}>
          <Field label="Name *"><input style={inputStyle} value={catForm.name} onChange={e => setCatForm(f=>({...f,name:e.target.value}))} placeholder="Ex: Hands, Coordination, Grooves…"/></Field>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <Field label="Emoji"><input style={inputStyle} value={catForm.emoji} onChange={e => setCatForm(f=>({...f,emoji:e.target.value}))} placeholder="🥁"/></Field>
            <Field label="Color">
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <input type="color" value={catForm.color} onChange={e => setCatForm(f=>({...f,color:e.target.value}))} style={{ width:42, height:40, borderRadius:8, border:'1.5px solid #2E2924', background:'#1C1916', cursor:'pointer', padding:3 }}/>
                <input style={{ ...inputStyle, flex:1 }} value={catForm.color} onChange={e => setCatForm(f=>({...f,color:e.target.value}))} placeholder="#FF5500"/>
              </div>
            </Field>
          </div>
          <ImagePicker label="Category icon (PNG 512×512, transparent background)" currentUrl={catForm.image_url} onUpload={url => setCatForm(f=>({...f,image_url:url}))} bucket="categories" pathPrefix={`cat-${catForm.name||'new'}`}/>
        </Modal>
      )}

      {/* ── SUBCATEGORY MODAL ── */}
      {subModal && (
        <Modal title={subForm.id?'EDITAR SUBCATEGORIA':'NOVA SUBCATEGORIA'} onClose={() => setSubModal(false)} onSave={saveSub} saving={saving}>
          <Field label="Category *">
            <select style={{ ...inputStyle, appearance:'none' }} value={subForm.category_id} onChange={e => setSubForm(f=>({...f,category_id:e.target.value}))}>
              <option value="">— Select —</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Name *"><input style={inputStyle} value={subForm.name} onChange={e => setSubForm(f=>({...f,name:e.target.value}))} placeholder="Ex: Rudiments, Equal Hands…"/></Field>
        </Modal>
      )}

      {/* ── EXERCISE MODAL ── */}
      {exModal && (
        <Modal title={exForm.id?'EDIT EXERCISE':'NEW EXERCISE'} onClose={() => setExModal(false)} onSave={saveEx} saving={saving}>
          <Field label="Title *"><input style={inputStyle} value={exForm.title} onChange={e => setExForm(f=>({...f,title:e.target.value}))} placeholder="Exercise name"/></Field>
          <Field label="Description"><textarea style={{ ...inputStyle, resize:'vertical' }} rows={3} value={exForm.description} onChange={e => setExForm(f=>({...f,description:e.target.value}))} placeholder="Exercise description"/></Field>
          <Field label="YouTube URL (optional)"><input style={inputStyle} value={exForm.youtube_url} onChange={e => setExForm(f=>({...f,youtube_url:e.target.value}))} placeholder="https://youtube.com/watch?v=..."/></Field>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <Field label="Category">
              <select style={{ ...inputStyle, appearance:'none' }} value={exForm.category_id} onChange={e => setExForm(f=>({...f,category_id:e.target.value,subcategory_id:''}))}>
                <option value="">— Select —</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Subcategory">
              <select style={{ ...inputStyle, appearance:'none' }} value={exForm.subcategory_id} onChange={e => setExForm(f=>({...f,subcategory_id:e.target.value}))}>
                <option value="">— Select —</option>
                {filteredSubs.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <Field label="Default BPM"><input type="number" style={inputStyle} min={20} max={300} value={exForm.default_bpm} onChange={e => setExForm(f=>({...f,default_bpm:e.target.value}))}/></Field>
            <Field label="Time Signature">
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {['2/4','3/4','4/4','5/4','6/8','7/8','9/8','12/8'].map(ts => (
                  <button key={ts} onClick={() => setExForm(f=>({...f,default_time_sig:ts}))}
                    style={{ padding:'6px 12px', borderRadius:8, border:`1.5px solid ${exForm.default_time_sig===ts?'#FF5500':'#2A2520'}`, background:exForm.default_time_sig===ts?'#FF5500':'#1C1916', color:exForm.default_time_sig===ts?'#fff':'#9A9088', fontFamily:"'Barlow Condensed'", fontWeight:700, fontSize:13, cursor:'pointer' }}>
                    {ts}
                  </button>
                ))}
              </div>
            </Field>
            <Field label={`Difficulty — ${DIFF_LABELS[exForm.difficulty]}`}>
              <input type="range" min={1} max={5} value={exForm.difficulty} onChange={e => setExForm(f=>({...f,difficulty:Number(e.target.value)}))} style={{ width:'100%', accentColor:DIFF_COLORS[exForm.difficulty], marginTop:12 }}/>
            </Field>
          </div>
          <ImagePicker label="Exercise image" currentUrl={exForm.image_url} onUpload={url => setExForm(f=>({...f,image_url:url}))} bucket="exercises" pathPrefix={`ex-${Date.now()}`}/>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <button onClick={() => setExForm(f=>({...f,active:!f.active}))} style={{ width:40, height:22, borderRadius:11, background:exForm.active?'#FF5500':'#2A2520', position:'relative', border:'none', cursor:'pointer', transition:'background 0.2s', flexShrink:0 }}>
              <div style={{ position:'absolute', width:16, height:16, borderRadius:'50%', background:'#fff', top:3, left:exForm.active?21:3, transition:'left 0.2s' }}/>
            </button>
            <span style={{ fontSize:13, color:'#9A9088' }}>{exForm.active?'Visible to users':'Hidden'}</span>
          </div>
        </Modal>
      )}

      {/* USERS TAB */}
      {tab === 'users' && (
        <div style={{ padding:'0 16px' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
            <div style={{ fontFamily:"'Barlow Condensed'", fontSize:13, fontWeight:700, color:'#7A7268', letterSpacing:1 }}>
              {users.length} REGISTERED USER{users.length !== 1 ? 'S' : ''}
            </div>
            <button onClick={loadUsers} style={{ background:'#2A2520', color:'#C4B9AC', padding:'6px 12px', borderRadius:8, fontFamily:"'Barlow Condensed'", fontWeight:700, fontSize:11, letterSpacing:1, border:'none', cursor:'pointer' }}>
              ↻ REFRESH
            </button>
          </div>
          {loadingUsers ? (
            <div style={{ textAlign:'center', padding:'40px 0', color:'#5A5248' }}>Loading...</div>
          ) : users.length === 0 ? (
            <div style={{ textAlign:'center', padding:'40px 0', color:'#5A5248' }}>No users yet.</div>
          ) : (
            users.map(u => {
              const trialEnd = u.trial_ends_at ? new Date(u.trial_ends_at) : null;
              const trialActive = trialEnd && trialEnd > new Date();
              const trialDaysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - new Date()) / 86400000)) : null;
              const isPro = u.plan === 'pro' || u.plan === 'teacher';
              const isFree = !isPro;

              let statusColor = '#5A5248';
              let statusLabel = 'Free';
              if (u.plan === 'teacher') { statusColor = '#7B2FBE'; statusLabel = 'Teacher'; }
              else if (u.plan === 'pro') { statusColor = '#22C55E'; statusLabel = 'Pro'; }
              else if (trialActive) { statusColor = '#F9A825'; statusLabel = `Trial · ${trialDaysLeft}d left`; }
              else { statusColor = '#FF4444'; statusLabel = 'Trial expired'; }

              return (
                <div key={u.id} style={{ background:'#1C1916', border:'1px solid #2A2520', borderRadius:12, padding:'14px 16px', marginBottom:10 }}>
                  <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:10 }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:14, fontWeight:700, color:'#F0EBE3', marginBottom:2, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{u.name || '—'}</div>
                      <div style={{ fontSize:12, color:'#7A7268', marginBottom:6, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{u.email}</div>
                      <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                        <span style={{ fontSize:11, fontFamily:"'Barlow Condensed'", fontWeight:700, color:statusColor, background:statusColor+'18', padding:'3px 8px', borderRadius:20, letterSpacing:0.5 }}>
                          {statusLabel}
                        </span>
                        {u.plan_granted_by && (
                          <span style={{ fontSize:11, fontFamily:"'Barlow Condensed'", fontWeight:700, color:'#5A5248', background:'#1C1916', padding:'3px 8px', borderRadius:20, border:'1px solid #2A2520' }}>
                            via {u.plan_granted_by}
                          </span>
                        )}
                        <span style={{ fontSize:11, color:'#3A3530' }}>
                          joined {new Date(u.created_at).toLocaleDateString('en-GB')}
                        </span>
                      </div>
                    </div>
                    {/* Actions */}
                    <div style={{ display:'flex', flexDirection:'column', gap:6, flexShrink:0 }}>
                      {updatingUser === u.id ? (
                        <div style={{ fontSize:12, color:'#FF5500', fontFamily:"'Barlow Condensed'", fontWeight:700 }}>SAVING…</div>
                      ) : isPro ? (
                        <button onClick={() => updateUserPlan(u.id, 'free', 'admin')}
                          style={{ background:'#2A1010', color:'#FF6060', border:'1px solid #FF444433', borderRadius:8, padding:'6px 10px', fontFamily:"'Barlow Condensed'", fontWeight:700, fontSize:11, letterSpacing:0.5, cursor:'pointer', whiteSpace:'nowrap' }}>
                          REMOVE PRO
                        </button>
                      ) : (
                        <button onClick={() => updateUserPlan(u.id, 'pro', 'admin')}
                            style={{ background:'#0F2318', color:'#22C55E', border:'1px solid #22C55E33', borderRadius:8, padding:'6px 10px', fontFamily:"'Barlow Condensed'", fontWeight:700, fontSize:11, letterSpacing:0.5, cursor:'pointer', whiteSpace:'nowrap' }}>
                            GIVE PRO
                          </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
